/*
 * -----------------------------------------------------------------------
 *  <copyright file="add-library-controls-risk.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import LibraryGrid from '../library-grid';
import { LibraryControlFieldsExtended } from '@interfaces/library-control.interface';
import { useBoolean, useGridCells, useTranslation } from 'hooks';
import {
    getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk,
    getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk_libraryControls_LibraryControl,
} from '@queries/generated/legacy-risk-assessment.generated';
import Constants from 'utils/constants';
import { getAvailableControls } from 'utils/filter';
import Button from 'views/components/core/buttons';
import { CustomDialog, DialogActions, DialogContent, DialogTitle } from 'views/components/core/dialog';
import DialogInformationBox from 'views/components/core/dialog-information-box/dialog-information-box';
import { GridColumnMenuActiveFilter } from 'views/components/core/grid-filters';

import './styles.scss';

interface IAddLibraryControlsRisk {
    open: boolean;
    onClose: (action?: string | undefined) => void;
    libraryControls: getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk_libraryControls_LibraryControl[];
    controls: getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk_libraryControls_LibraryControl[];
    systemRisks: getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk[];
    currentRisk: getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk;
    onAssign: (closeModal?: boolean, selectedItems?: LibraryControlFieldsExtended[]) => void;
    showNew: boolean;
    openNew: () => void;
    onLoading: boolean;
    showAddControls: boolean;
    isSystem: boolean;
}

type LibraryControlsInfo = {
    allControlsLength: number;
    filteredControls: LibraryControlFieldsExtended[];
};

const AddLibraryControlsRisk = ({
    open,
    onClose,
    controls,
    libraryControls,
    currentRisk,
    onAssign,
    systemRisks,
    showNew,
    openNew,
    onLoading,
    showAddControls,
    isSystem,
}: IAddLibraryControlsRisk) => {
    const { t } = useTranslation();

    const [selectedItems, setSelectedItems] = useState<LibraryControlFieldsExtended[]>([]);
    const [filteredControls, setFilteredControls] = useState<LibraryControlsInfo>({
        allControlsLength: 0,
        filteredControls: [],
    });

    const includeLibrary = useBoolean(true);

    const { MaxLengthCell, YesNoCell } = useGridCells();

    const isAdding = isSystem && showAddControls;

    const gridColumns = [
        {
            title: t('ra:description'),
            id: 'item-description',
            field: 'description',
            cells: { data: MaxLengthCell(150, '30vh') },
        },
        {
            title: t('ra:library'),
            id: 'item-library',
            field: 'library',
            width: 105,
            hideFilter: showAddControls,
            cells: { data: YesNoCell },
            filterUi: !showAddControls ? GridColumnMenuActiveFilter : undefined,
        },
        ...(!showAddControls
            ? [
                  {
                      title: t('ra:linked'),
                      id: 'item-linked',
                      field: 'isLinkIcon',
                      width: 105,
                      cells: { data: YesNoCell },
                      filterUi: GridColumnMenuActiveFilter,
                  },
              ]
            : []),
    ];

    const close = () => {
        setSelectedItems([]);
        onClose();
        includeLibrary.setTrue();
    };

    const addItems = (closeModal?: boolean) => {
        onAssign(closeModal, selectedItems);
    };

    useEffect(() => {
        if (!open) {
            setSelectedItems([]);
            includeLibrary.setTrue();
        }
    }, [open]);

    useEffect(() => {
        if (open) {
            let originRisk: any;
            if (
                !isAdding &&
                controls?.length &&
                libraryControls?.length &&
                currentRisk?.systemReferenceId !== Constants.ObjectId.Empty
            )
                originRisk = systemRisks.find((item: any) => item.id === currentRisk?.systemReferenceId);
            const array = getAvailableControls(
                libraryControls,
                controls,
                currentRisk,
                includeLibrary.value,
                '',
                originRisk,
                isSystem,
            );
            setFilteredControls(array);
        }
    }, [libraryControls, controls, open, includeLibrary, currentRisk]);

    useEffect(() => {
        if (open)
            setSelectedItems(
                selectedItems.filter(
                    (item: any) => filteredControls.filteredControls.findIndex((el: any) => el.id === item.id) >= 0,
                ),
            );
    }, [filteredControls, open]);

    if (!open) return null;

    return (
        <CustomDialog
            onClose={close}
            fullWidth
            scroll="paper"
            maxWidth="md"
            open
            TransitionProps={{
                onEntering: () => {
                    // onEntering callback is called while rendering/entering dialog
                    // check if there are controls to associate
                    // if not then open new control dialog
                    if (!filteredControls.allControlsLength && open) onClose('open-add');
                },
            }}
        >
            <DialogTitle id="add-library-control-title">
                {!showAddControls ? t('ra:assignControls') : t('ra:addControls')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={close}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent className="add-controls-dialog-content pb-0 mb-20">
                <div className="info-box-wrapper">
                    <DialogInformationBox
                        topText={t(
                            showAddControls ? 'ra:addLibraryRisksInformation' : 'ra:assignLibraryRisksInformation',
                        )}
                    />
                </div>
                {!!currentRisk && (
                    <p className="description">
                        <span className="title">{t('ra:statement')}: </span>
                        {currentRisk && currentRisk.statement}
                    </p>
                )}
                <LibraryGrid
                    items={filteredControls?.filteredControls}
                    columns={gridColumns}
                    itemsType={t('ra:controlRecords')}
                    selection={{
                        selectedItems,
                        setSelectedItems,
                        showItemsCount: true,
                    }}
                />
            </DialogContent>
            <DialogActions className="actions-add-risk assign-items flex-wrap pt-0">
                {!!showNew && (
                    <Button
                        disabled={onLoading}
                        id="add-library-control-new"
                        text="new"
                        onClick={() => {
                            openNew();
                        }}
                    />
                )}
                <Button
                    text={showAddControls ? 'add' : 'assign'}
                    id="add-library-control-save"
                    disabled={!selectedItems.length || onLoading}
                    onClick={() => addItems(false)}
                />
                <Button
                    text={showAddControls ? 'addAndClose' : 'assignClose'}
                    id="add-library-control-save-close"
                    disabled={!selectedItems.length || onLoading}
                    onClick={() => addItems(true)}
                />
            </DialogActions>
        </CustomDialog>
    );
};

export default AddLibraryControlsRisk;
